import React, { useMemo, useState, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

export interface LtwGame {
  game_date?: string;
  season?: number;
  opponent: string;
  game_result: number | null;
  stat_value: number;
}

export interface LtwPlayer {
  player_id: string;
  player_name: string;
  team_name: string;
  games: LtwGame[];
}

export interface LtwGraphData {
  query_type: string;
  players?: LtwPlayer[];
}

interface LtwGraphProps {
  data: LtwGraphData;
  chartReady?: boolean;
  statLabel?: string;
}

const PALETTE = [
  "#3B82F6","#10B981","#F59E0B","#EC4899",
  "#8B5CF6","#06B6D4","#F97316","#14B8A6","#EF4444","#84CC16",
];

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&family=JetBrains+Mono:wght@400;500;600&display=swap');

  .ltw * { box-sizing: border-box; }

  .ltw {
    --bg: #ffffff;
    --surface: #f8fafc;
    --border: #e8edf3;
    --border-accent: #cbd5e1;
    --text-primary: #0f172a;
    --text-secondary: #475569;
    --text-muted: #94a3b8;
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: var(--bg);
    border-radius: 24px;
    padding: 24px 28px;
    position: relative;
    width: 100%;
    border: 1px solid var(--border);
    box-shadow:
      0 1px 2px rgba(0,0,0,0.04),
      0 4px 16px rgba(0,0,0,0.06),
      0 16px 48px rgba(0,0,0,0.04);
  }

  .ltw::before {
    content: '';
    position: absolute;
    top: 0; right: 0;
    width: 260px; height: 160px;
    background: radial-gradient(ellipse at top right, rgba(59,130,246,0.05) 0%, transparent 65%);
    pointer-events: none;
    border-radius: 0 24px 0 0;
  }

  .ltw::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, rgba(148,163,184,0.18) 1px, transparent 1px);
    background-size: 28px 28px;
    pointer-events: none;
    border-radius: 24px;
    opacity: 0.55;
    z-index: 0;
  }

  .ltw-chips-wrapper { position: relative; z-index: 2; margin-bottom: 18px; }

  .ltw-chips {
    display: flex; gap: 6px; flex-wrap: wrap;
    padding: 12px 14px;
    background: var(--surface);
    border-radius: 14px;
    border: 1px solid var(--border);
  }

  .ltw-chip-all {
    display: flex; align-items: center; gap: 5px;
    padding: 6px 13px; border-radius: 9px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 11px; font-weight: 700;
    border: 1.5px solid var(--border-accent);
    background: white; color: var(--text-secondary);
    cursor: pointer; transition: all 0.18s ease; white-space: nowrap;
  }
  .ltw-chip-all:hover { background: #f0f9ff; border-color: #93c5fd; color: #2563eb; }
  .ltw-chip-all.active { background: #eff6ff; border-color: #93c5fd; color: #2563eb; }

  .ltw-chip {
    display: flex; align-items: center; gap: 7px;
    padding: 5px 10px 5px 8px; border-radius: 9px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 11px; font-weight: 600;
    border: 1.5px solid transparent;
    background: white; color: var(--text-secondary);
    cursor: pointer; transition: all 0.18s cubic-bezier(0.4,0,0.2,1);
    white-space: nowrap; height: 34px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }
  .ltw-chip:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.08); color: var(--text-primary); }
  .ltw-chip.active {
    border-color: var(--chip-border);
    background: var(--chip-bg);
    color: var(--chip-color);
    box-shadow: 0 2px 8px var(--chip-shadow);
  }
  .ltw-swatch {
    width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .ltw-chip.active .ltw-swatch { transform: scale(1.4); box-shadow: 0 0 6px var(--chip-color); }
  .ltw-chip-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 110px; }
  .ltw-chip-stat-badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px; font-weight: 600;
    padding: 2px 7px; border-radius: 6px;
    background: rgba(0,0,0,0.06); color: var(--text-secondary);
    flex-shrink: 0; transition: background 0.18s ease, color 0.18s ease;
  }
  .ltw-chip.active .ltw-chip-stat-badge { background: var(--chip-color); color: #ffffff; }
  .ltw-chip-check { font-size: 10px; font-weight: 700; flex-shrink: 0; }

  .ltw-chart-wrapper {
    position: relative; z-index: 2;
    height: 380px; width: 100%;
    border-radius: 14px; overflow: visible;
    background: white; border: 1px solid var(--border);
  }
  .ltw-chart-wrapper .apexcharts-tooltip {
    z-index: 9999 !important; overflow: visible !important;
  }
  .ltw-empty {
    height: 100%; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    color: var(--text-muted);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px; font-weight: 600; gap: 8px;
  }
  .ltw-empty-icon { font-size: 28px; opacity: 0.35; }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatPlayerName(raw: string): string {
  if (!raw) return "";

  let name = raw;

  // Strip markdown link: [WASHINGTON, MIKE](url) → WASHINGTON, MIKE
  const markdownMatch = raw.match(/\[([^\]]+)\]\([^)]*\)/);
  if (markdownMatch) {
    name = markdownMatch[1];
  }

  // Strip HTML anchor
  const htmlMatch = name.match(/<a[^>]*>([^<]+)<\/a>/i);
  if (htmlMatch) {
    name = htmlMatch[1];
  }

  name = name.trim();

  // "WASHINGTON, MIKE" → "Washington, Mike" (just title case, keep comma, NO swap)
  const toTitle = (s: string) =>
    s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

  return name
    .split(" ")
    .filter(Boolean)
    .map(toTitle)
    .join(" ");
}

function detectSeasonMode(players: LtwPlayer[]): boolean {
  for (const p of players) {
    for (const g of p.games) {
      if (g.game_date) {
        return /^\d{4}$/.test(g.game_date.trim());
      }
      if (g.season != null) return true;
    }
  }
  return false;
}

function gameKey(g: LtwGame, isSeasonMode: boolean): number {
  if (isSeasonMode) {
    if (g.season != null) return g.season;
    if (g.game_date) return parseInt(g.game_date, 10);
    return 0;
  }
  if (g.game_date) {
    const parts = g.game_date.split("T")[0].split("-").map(Number);
    return Date.UTC(parts[0], parts[1] - 1, parts[2]);
  }
  return 0;
}

function gameLabel(g: LtwGame, isSeasonMode: boolean): string {
  if (isSeasonMode) {
    return g.season != null ? String(g.season) : (g.game_date ?? "");
  }
  if (g.game_date) {
    const parts = g.game_date.split("T")[0].split("-").map(Number);
    const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    const mon = d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
    const yy = String(d.getUTCFullYear()).slice(-2);
    return `${mon}-${yy}`;
  }
  return "";
}

function gameLabelSingle(g: LtwGame, isSeasonMode: boolean): string {
  if (isSeasonMode) {
    return g.season != null ? String(g.season) : (g.game_date ?? "");
  }
  if (g.game_date) {
    const parts = g.game_date.split("T")[0].split("-").map(Number);
    const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const yy = String(d.getUTCFullYear()).slice(-2);
    return `${dd}-${mm}-${yy}`;
  }
  return "";
}

function formatDisplayDate(g: LtwGame, isSeasonMode: boolean): string {
  if (isSeasonMode) {
    return g.season != null ? String(g.season) : (g.game_date ?? "");
  }
  if (g.game_date) {
    const parts = g.game_date.split("T")[0].split("-").map(Number);
    const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    return d.toLocaleDateString("en-US", {
      day: "2-digit", month: "short", year: "numeric", timeZone: "UTC",
    });
  }
  return "";
}

// ── Component ─────────────────────────────────────────────────────────────────

const LtwGraph: React.FC<LtwGraphProps> = ({
  data,
  chartReady = true,
  statLabel = "Stat Value",
}) => {
  // ✅ KEY FIX: Normalize all player_name values at entry point
  const players = useMemo(() => {
    return (data?.players || []).map(p => ({
      ...p,
      player_name: formatPlayerName(p.player_name),
    }));
  }, [data]);

  const isSeasonMode = useMemo(() => detectSeasonMode(players), [players]);

  const [selectedPlayers, setSelectedPlayers] = useState<Set<number>>(
    () => new Set(players.length ? [0] : [])
  );

  const togglePlayer = useCallback((idx: number) => {
    setSelectedPlayers(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }, []);

  const selectAllPlayers = useCallback(() => {
    setSelectedPlayers(prev =>
      prev.size === players.length
        ? new Set<number>()
        : new Set(Array.from({ length: players.length }, (_, i) => i))
    );
  }, [players.length]);

  const selectedIndicesOrdered = useMemo(
    () => Array.from(selectedPlayers).sort((a, b) => a - b),
    [selectedPlayers]
  );

  const isSinglePlayer = selectedPlayers.size === 1;

  // ── Build global x‑axis from selected players only ─────────────────────────
  const { xCategories, xCategoryKeys, playerGameMaps, seriesData } = useMemo(() => {
    const keySet = new Set<number>();
    for (const idx of selectedIndicesOrdered) {
      for (const g of players[idx].games) {
        keySet.add(gameKey(g, isSeasonMode));
      }
    }

    const sortedKeys = Array.from(keySet).sort((a, b) => a - b);

    const categories = sortedKeys.map(k => {
      let representativeGame: LtwGame | undefined;
      for (const idx of selectedIndicesOrdered) {
        const game = players[idx].games.find(g => gameKey(g, isSeasonMode) === k);
        if (game) {
          representativeGame = game;
          break;
        }
      }
      if (!representativeGame) return "";
      return isSinglePlayer
        ? gameLabelSingle(representativeGame, isSeasonMode)
        : gameLabel(representativeGame, isSeasonMode);
    });

    const valueMaps: Map<number, number>[] = [];
    const gameMaps: Map<number, LtwGame>[] = [];

    for (const idx of selectedIndicesOrdered) {
      const vMap = new Map<number, number>();
      const gMap = new Map<number, LtwGame>();
      for (const g of players[idx].games) {
        const k = gameKey(g, isSeasonMode);
        vMap.set(k, g.stat_value);
        gMap.set(k, g);
      }
      valueMaps.push(vMap);
      gameMaps.push(gMap);
    }

    // player_name is already formatted — use directly
    const series = selectedIndicesOrdered.map((_, seriesIdx) => ({
      name: players[selectedIndicesOrdered[seriesIdx]].player_name,
      data: sortedKeys.map(k => valueMaps[seriesIdx].get(k) ?? null),
    }));

    return {
      xCategories: categories,
      xCategoryKeys: sortedKeys,
      playerGameMaps: gameMaps,
      seriesData: series,
    };
  }, [players, selectedIndicesOrdered, isSeasonMode, isSinglePlayer]);

  // ── ApexCharts options ──────────────────────────────────────────────────────
  const options: ApexOptions = useMemo((): ApexOptions => ({
    chart: {
      type: "line",
      background: "transparent",
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      animations: { enabled: true, speed: 650, easing: "easeinout" },
      height: 380,
      offsetX: 0,
      offsetY: 0,
      sparkline: { enabled: false },
    },
    colors: selectedIndicesOrdered.map(i => PALETTE[i % PALETTE.length]),
    stroke: { curve: "smooth", width: 2.5, lineCap: "round" },
    markers: {
      size: 5,
      strokeWidth: 2.5,
      strokeColors: "#ffffff",
      strokeOpacity: 1,
      hover: { size: 8 },
    },
    xaxis: {
      type: "category",
      categories: xCategories,
      tickPlacement: "on",
      tickAmount: Math.min(xCategories.length, 10),
      labels: {
        style: {
          colors: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        rotate: 0,
        rotateAlways: false,
        hideOverlappingLabels: true,
        offsetX: 0,
        offsetY: 0,
      },
      axisBorder: { show: true, color: "#e8edf3", height: 1 },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      title: {
        text: isSeasonMode ? "Season" : "Game Date",
        style: {
          color: "#94a3b8", fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace", fontWeight: "500",
        },
        offsetY: 6,
      },
      crosshairs: { show: true },
    },
    yaxis: {
      show: true,
      labels: {
        show: true,
        minWidth: 28,
        maxWidth: 60,
        style: {
          colors: ["#94a3b8"],
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        formatter: (val: number) => (val != null ? val.toFixed(0) : ""),
        offsetX: 0,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      title: {
        text: statLabel,
        style: {
          color: "#94a3b8", fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace", fontWeight: "500",
        },
        offsetX: 0,
      },
    },
    grid: {
      show: true,
      borderColor: "#f1f5f9",
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 10, right: 20, bottom: 4, left: 0 },
    },
    legend: { show: false },
    tooltip: {
      theme: "light",
      shared: false,
      intersect: true,
      x: { show: false },
      custom: ({ seriesIndex, dataPointIndex }) => {
        if (seriesIndex >= selectedIndicesOrdered.length) return "";
        const playerIdx = selectedIndicesOrdered[seriesIndex];
        const player = players[playerIdx];
        const color = PALETTE[playerIdx % PALETTE.length];
        const key = xCategoryKeys[dataPointIndex];
        const game = playerGameMaps[seriesIndex]?.get(key);
        if (!game) return "";
        return buildTooltip(player, game, color, statLabel, isSeasonMode);
      },
    },
    dataLabels: { enabled: false },
  }), [
    players, statLabel, selectedIndicesOrdered, xCategories, xCategoryKeys,
    playerGameMaps, isSeasonMode
  ]);

  if (!players || players.length === 0) {
    return (
      <>
        <style>{css}</style>
        <div className="ltw">
          <div className="ltw-empty">
            <span className="ltw-empty-icon">📭</span>
            <span>No data available</span>
          </div>
        </div>
      </>
    );
  }

  const allSelected = selectedPlayers.size === players.length;

  return (
    <>
      <style>{css}</style>
      <div className="ltw">
        <div className="ltw-chips-wrapper">
          <div className="ltw-chips">
            <button
              className={`ltw-chip-all${allSelected ? " active" : ""}`}
              onClick={selectAllPlayers}
            >
              {allSelected ? "✕ Clear" : "⊕ All"}
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px" }}>
                ({players.length})
              </span>
            </button>

            {players.map((player, idx) => {
              const color = PALETTE[idx % PALETTE.length];
              const active = selectedPlayers.has(idx);
              return (
                <button
                  key={idx}
                  className={`ltw-chip${active ? " active" : ""}`}
                  onClick={() => togglePlayer(idx)}
                  style={{
                    ["--chip-color" as any]: color,
                    ["--chip-bg" as any]: `${color}12`,
                    ["--chip-border" as any]: `${color}40`,
                    ["--chip-shadow" as any]: `${color}18`,
                  }}
                >
                  <span className="ltw-swatch" style={{ background: active ? color : "#cbd5e1" }} />
                  {/* player_name already formatted — render directly */}
                  <span className="ltw-chip-name">{player.player_name}</span>
                  {active && <span className="ltw-chip-check" style={{ color }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="ltw-chart-wrapper">
          {seriesData.length > 0 && chartReady ? (
            <ReactApexChart
              type="line"
              series={seriesData}
              options={options}
              height={380}
            />
          ) : (
            <div className="ltw-empty">
              <span className="ltw-empty-icon">↑</span>
              <span>Select players above to view trends</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ── Tooltip ───────────────────────────────────────────────────────────────────

function buildTooltip(
  player: LtwPlayer,
  game: LtwGame,
  color: string,
  statLabel: string,
  isSeasonMode: boolean
): string {
  const displayDate = formatDisplayDate(game, isSeasonMode);

  const opponentRow = isSeasonMode ? "" : `
    <div style="background:#f8fafc;border-radius:8px;padding:7px 9px;border:1px solid #f1f5f9;">
      <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:3px;">vs</div>
      <div style="font-size:11px;font-weight:600;color:#475569;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${game.opponent || "N/A"}</div>
    </div>`;

  const rc = game.game_result === 1 ? "#10b981" : "#ef4444";
  const rt = game.game_result === 1 ? "● Win" : "● Loss";
  const resultRow = (isSeasonMode || game.game_result == null) ? "" : `
    <div style="margin-top:8px;text-align:right;">
      <span style="font-size:10px;font-weight:700;color:${rc};">${rt}</span>
    </div>`;

  return `
    <div style="background:#fff;border:1px solid #e2e8f0;border-top:3px solid ${color};border-radius:14px;padding:13px 15px;font-family:'Plus Jakarta Sans',sans-serif;box-shadow:0 8px 30px rgba(0,0,0,0.12),0 2px 8px rgba(0,0,0,0.06);min-width:205px;max-width:245px;z-index:9999;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
        <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></div>
        <span style="font-weight:700;color:#0f172a;font-size:13px;letter-spacing:-0.01em;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${player.player_name}</span>
      </div>
      <div style="display:grid;grid-template-columns:${isSeasonMode ? "1fr" : "1fr 1fr"};gap:6px;margin-bottom:10px;">
        <div style="background:#f8fafc;border-radius:8px;padding:7px 9px;border:1px solid #f1f5f9;">
          <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:3px;">${isSeasonMode ? "Season" : "Date"}</div>
          <div style="font-size:11px;font-weight:600;color:#475569;">${displayDate}</div>
        </div>
        ${opponentRow}
      </div>
      <div style="display:flex;align-items:center;justify-content:space-between;background:${color}10;border:1px solid ${color}25;border-radius:10px;padding:9px 13px;">
        <span style="font-family:'JetBrains Mono',monospace;font-size:9px;color:#64748b;text-transform:uppercase;letter-spacing:0.08em;">${statLabel}</span>
        <span style="font-size:22px;font-weight:800;color:${color};letter-spacing:-0.02em;">${game.stat_value}</span>
      </div>
      ${resultRow}
    </div>`;
}

export default LtwGraph;